import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { todayISO } from "@/engine/date";
import { daysUntil, formatDate, formatDaysUntil } from "@/lib/date-format";
import {
  archiveWorkspace,
  canLeaveWorkspace,
  leaveWorkspace,
  leaveWorkspaceImpact,
  restoreWorkspaceArchive,
  restoreWorkspaceTrash,
  transferImpact,
  transferWorkspace,
  trashWorkspace,
  trashWorkspaceImpact,
  useWorkspaceDanger,
} from "./workspace-danger";
import { useWorkspaceMembers } from "./workspace-access";

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  dangerCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  dangerHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  dangerTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPaletteRedForeground1,
  },
  dangerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "12px 0",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    ":last-child": { borderBottom: "none" },
  },
  dangerRowText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    flex: 1,
  },
  dangerRowTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  trashCard: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  trash: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
  },
  frame: { width: "100%", maxHeight: "100%", overflow: "auto" },
  fixed: { tableLayout: "fixed" },
  clip: { overflow: "hidden", maxWidth: 0 },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  soon: { color: tokens.colorPaletteDarkOrangeForeground1 },
  danger: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
});

/** 워크스페이스 설정의 맨 끝 탭. 목록에서 워크스페이스를 빼지는 않는다. */
export function WorkspaceDangerPanel({ workspaceName, role }: { workspaceName: string; role: string }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const danger = useWorkspaceDanger();
  const members = useWorkspaceMembers();
  const target = members.find((member) => member.role === "admin") ?? null;
  const owner = role === "owner";
  const [pending, setPending] = useState<"archive" | "transfer" | "leave" | "delete" | "restore" | null>(null);
  const [trashId, setTrashId] = useState<string | null>(null);
  const trashItem = danger.trash.find((item) => item.id === trashId) ?? null;
  const canLeave = canLeaveWorkspace(role, danger.transferredTo != null);

  return (
    <div className={styles.stack}>
      {/* 1. 위험 구역 카드 */}
      <div className={styles.dangerCard}>
        <div className={styles.dangerHead}>
          <div className={styles.dangerTitle}>위험 구역 (Danger Zone)</div>
          <Caption1 className={styles.soon}>
            아래 작업은 되돌릴 수 없거나 멤버들의 접근 권한에 중대한 영향을 줍니다. 주의해서 진행해 주세요.
          </Caption1>
        </div>

        {owner ? (
          danger.archived ? (
            <div className={styles.dangerRow}>
              <div className={styles.dangerRowText}>
                <div className={styles.dangerRowTitle}>워크스페이스 복구</div>
                <Caption1>현재 아카이브된 읽기 전용 상태입니다. 복구하면 다시 편집할 수 있습니다.</Caption1>
              </div>
              <FuiButton appearance="outline" density="dialog" onClick={() => setPending("restore")}>
                복구
              </FuiButton>
            </div>
          ) : (
            <div className={styles.dangerRow}>
              <div className={styles.dangerRowText}>
                <div className={styles.dangerRowTitle}>워크스페이스 아카이브</div>
                <Caption1>읽기 전용으로 아카이브합니다. 플랜 및 작업 내용은 보존되며 언제든 복구할 수 있습니다.</Caption1>
              </div>
              <FuiButton appearance="outline" density="dialog" onClick={() => setPending("archive")}>
                아카이브
              </FuiButton>
            </div>
          )
        ) : null}

        {owner && !danger.transferredTo ? (
          <div className={styles.dangerRow}>
            <div className={styles.dangerRowText}>
              <div className={styles.dangerRowTitle}>소유권 이전</div>
              <Caption1>
                {target ? `소유권을 관리자 「${target.name}」에게 넘깁니다.` : "활성 관리자가 없으면 소유권을 넘길 수 없습니다."}
              </Caption1>
            </div>
            <FuiButton appearance="outline" density="dialog" disabled={!target} onClick={() => setPending("transfer")}>
              소유권 이전
            </FuiButton>
          </div>
        ) : null}

        {owner && danger.transferredTo ? (
          <div className={styles.dangerRow}>
            <div className={styles.dangerRowText}>
              <div className={styles.dangerRowTitle}>소유권 이전됨</div>
              <Caption1>소유권은 「{danger.transferredTo}」에게 넘어갔습니다. 현재 내 권한은 관리자입니다.</Caption1>
            </div>
          </div>
        ) : null}

        {owner ? (
          <div className={styles.dangerRow}>
            <div className={styles.dangerRowText}>
              <div className={styles.dangerRowTitle}>워크스페이스 삭제</div>
              <Caption1>이 워크스페이스를 휴지통에 넣습니다. {trashWorkspaceImpact()}</Caption1>
            </div>
            <FuiButton appearance="outline" density="dialog" onClick={() => setPending("delete")}>
              삭제
            </FuiButton>
          </div>
        ) : null}

        <div className={styles.dangerRow}>
          <div className={styles.dangerRowText}>
            <div className={styles.dangerRowTitle}>워크스페이스 나가기</div>
            <Caption1>
              {owner
                ? "소유자는 소유권을 이전한 뒤에만 나갈 수 있습니다."
                : danger.departed
                ? `이 워크스페이스에서 나갔습니다. ${leaveWorkspaceImpact()}`
                : "워크스페이스 참여 목록에서 제외되며 공유된 플랜에 더 이상 접근할 수 없습니다."}
            </Caption1>
          </div>
          {canLeave && !danger.departed ? (
            <FuiButton appearance="outline" density="dialog" onClick={() => setPending("leave")}>
              나가기
            </FuiButton>
          ) : null}
        </div>
      </div>

      {/* 2. 휴지통 카드 */}
      {owner ? (
        <div className={styles.trashCard}>
          <div className={styles.dangerHead}>
            <div className={styles.dangerRowTitle}>휴지통</div>
            <Caption1>삭제된 플랜 및 데이터는 30일 동안 보관된 후 영구 삭제됩니다.</Caption1>
          </div>

          {danger.trash.length > 0 ? (
            <div className={mergeClasses(table.frame, styles.frame)}>
              <table className={mergeClasses(table.table, styles.fixed)} aria-label="휴지통">
                <thead>
                  <tr>
                    <th className={table.th}>이름</th>
                    <th className={table.th}>삭제자</th>
                    <th className={mergeClasses(table.th, table.num)}>삭제일</th>
                    <th className={mergeClasses(table.th, table.num)}>영구 삭제 예정</th>
                    <th className={table.th} />
                  </tr>
                </thead>
                <tbody>
                  {danger.trash.map((item) => {
                    const left = daysUntil(item.purge, todayISO());
                    const soon = left != null && left <= 7;
                    return (
                      <tr key={item.id}>
                        <td className={mergeClasses(table.td, styles.clip)}>
                          <div className={styles.name} title={item.name}>{item.name}</div>
                        </td>
                        <td className={table.td}>{item.by}</td>
                        <td className={mergeClasses(table.td, table.num)}>{formatDate(item.deleted, "numeric")}</td>
                        <td className={mergeClasses(table.td, table.num, soon && styles.soon)}>
                          {formatDate(item.purge, "numeric")} {formatDaysUntil(item.purge, todayISO())}
                        </td>
                        <td className={table.td}>
                          <FuiButton appearance="subtle" density="dialog" onClick={() => setTrashId(item.id)}>
                            복구
                          </FuiButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <GhostEmptyState compact title="휴지통이 비어 있습니다" />
          )}
        </div>
      ) : null}
      <ConfirmDialog
        open={pending === "archive"}
        title="워크스페이스 아카이브"
        message="이 워크스페이스는 읽기 전용이 됩니다. 언제든 복구할 수 있습니다."
        confirmLabel="아카이브"
        tier="T2"
        expectedName={workspaceName}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          archiveWorkspace();
          setPending(null);
        }}
      />
      <ConfirmDialog
        open={pending === "restore"}
        title="아카이브 복구"
        message="이 워크스페이스를 복구할까요?"
        confirmLabel="복구"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          restoreWorkspaceArchive();
          setPending(null);
        }}
      />
      <ConfirmDialog
        open={pending === "transfer" && target != null}
        title="소유권 이전"
        message={<>소유권을 이전할까요? {transferImpact()}</>}
        confirmLabel="이전"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (target) transferWorkspace(target.name);
          setPending(null);
        }}
      />
      <ConfirmDialog
        open={pending === "leave"}
        title="워크스페이스 나가기"
        message={<>이 워크스페이스를 나갈까요? {leaveWorkspaceImpact()}</>}
        confirmLabel="나가기"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          leaveWorkspace(role);
          setPending(null);
        }}
      />
      <ConfirmDialog
        open={pending === "delete"}
        title="워크스페이스 삭제"
        message={<>이 워크스페이스를 휴지통에 넣을까요? {trashWorkspaceImpact()}</>}
        confirmLabel="삭제"
        tier="T2"
        expectedName={workspaceName}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          trashWorkspace(workspaceName);
          setPending(null);
        }}
      />
      <ConfirmDialog
        open={trashId != null}
        title="휴지통 복구"
        message="이 워크스페이스를 복구할까요?"
        confirmLabel="복구"
        onCancel={() => setTrashId(null)}
        onConfirm={() => {
          if (trashItem) restoreWorkspaceTrash(trashItem.id);
          setTrashId(null);
        }}
      />
    </div>
  );
}
